/**
 * ============================================================================
 * StatusPipeline — Phase 6c frontend (CCIE).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: hand-rolled "Sankey-lite" inline-SVG visualisation showing the
 *          post-action queue distribution across QUEUED → RUNNING → COMPLETED /
 *          FAILED. We deliberately avoid a chart library (CCIE-001) — the
 *          shape is fixed, four states, four flows.
 *
 * A11Y:
 *   - role="img" with a synthesized aria-label that summarises the counts.
 *   - The same data is exposed as a parallel <ul> below the chart so SR
 *     users get a tabular reading without the SVG noise. Visually hidden.
 * ============================================================================ */

import { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './PostActionTracker.module.css';
import type { PostActionPipelineCounts } from './usePostActionsQuery';

export interface StatusPipelineProps {
  counts: PostActionPipelineCounts;
}

const W = 560;
const H = 130;
const PAD = 8;
const COL_W = 110;
const COL_GAP = 40;

const COLOR = {
  QUEUED: '#6B7280',
  RUNNING: '#2563EB',
  COMPLETED: '#1DD1A1',
  FAILED: '#EF4444',
} as const;

export function StatusPipeline({ counts }: StatusPipelineProps) {
  const { t, i18n } = useTranslation();
  const gradQueuedRunningId = useId();
  const gradRunningCompletedId = useId();
  const gradRunningFailedId = useId();

  const nf = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);

  const total =
    counts.QUEUED + counts.RUNNING + counts.COMPLETED + counts.FAILED;

  const scale = (n: number) => {
    if (total === 0) return 0;
    // Cap the band height so a 1-item pipeline still draws a band.
    const min = total > 0 ? 6 : 0;
    return Math.max(min, ((n / total) * (H - PAD * 2)) | 0);
  };

  // Column anchor x-positions (left edge of each stage).
  const x1 = PAD;
  const x2 = x1 + COL_W + COL_GAP;
  const x3 = x2 + COL_W + COL_GAP;
  const x4 = x3; // COMPLETED/FAILED share the final column visually.

  // Vertical positions for each stage's band centre.
  const yMid = H / 2;

  const hQueued = scale(counts.QUEUED);
  const hRunning = scale(counts.RUNNING);
  const hCompleted = scale(counts.COMPLETED);
  const hFailed = scale(counts.FAILED);

  const ariaSummary = t('changeControl.postActions.pipelineAria', {
    defaultValue:
      'Post-action pipeline — {{q}} queued, {{r}} running, {{c}} completed, {{f}} failed.',
    q: counts.QUEUED,
    r: counts.RUNNING,
    c: counts.COMPLETED,
    f: counts.FAILED,
  });

  return (
    <figure className={styles.pipelineFigure}>
      <svg
        className={styles.pipelineSvg}
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={ariaSummary}
      >
        <defs>
          <linearGradient id={gradQueuedRunningId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={COLOR.QUEUED} stopOpacity={0.6} />
            <stop offset="100%" stopColor={COLOR.RUNNING} stopOpacity={0.6} />
          </linearGradient>
          <linearGradient id={gradRunningCompletedId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={COLOR.RUNNING} stopOpacity={0.6} />
            <stop offset="100%" stopColor={COLOR.COMPLETED} stopOpacity={0.6} />
          </linearGradient>
          <linearGradient id={gradRunningFailedId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={COLOR.RUNNING} stopOpacity={0.6} />
            <stop offset="100%" stopColor={COLOR.FAILED} stopOpacity={0.6} />
          </linearGradient>
        </defs>

        {/* Flow band: QUEUED → RUNNING */}
        {hQueued + hRunning > 0 && (
          <path
            d={pathBetween(
              x1 + COL_W,
              yMid - hQueued / 2,
              x2,
              yMid - hRunning / 2,
              Math.min(hQueued, hRunning) || 4,
            )}
            fill={`url(#${gradQueuedRunningId})`}
          />
        )}
        {/* Flow band: RUNNING → COMPLETED (upper half) */}
        {hCompleted > 0 && (
          <path
            d={pathBetween(
              x2 + COL_W,
              yMid - hRunning / 2,
              x3,
              yMid - hCompleted - 2,
              hCompleted || 4,
            )}
            fill={`url(#${gradRunningCompletedId})`}
          />
        )}
        {/* Flow band: RUNNING → FAILED (lower half) */}
        {hFailed > 0 && (
          <path
            d={pathBetween(
              x2 + COL_W,
              yMid + hRunning / 2 - hFailed,
              x3,
              yMid + 2,
              hFailed || 4,
            )}
            fill={`url(#${gradRunningFailedId})`}
          />
        )}

        {/* QUEUED stage */}
        <rect
          x={x1}
          y={yMid - hQueued / 2}
          width={COL_W}
          height={hQueued}
          fill={COLOR.QUEUED}
          opacity={0.85}
          rx={4}
        />
        {/* RUNNING stage */}
        <rect
          x={x2}
          y={yMid - hRunning / 2}
          width={COL_W}
          height={hRunning}
          fill={COLOR.RUNNING}
          opacity={0.85}
          rx={4}
        />
        {/* COMPLETED stage (upper) */}
        <rect
          x={x3}
          y={yMid - hCompleted - 2}
          width={COL_W}
          height={hCompleted}
          fill={COLOR.COMPLETED}
          opacity={0.95}
          rx={4}
        />
        {/* FAILED stage (lower) */}
        <rect
          x={x4}
          y={yMid + 2}
          width={COL_W}
          height={hFailed}
          fill={COLOR.FAILED}
          opacity={0.95}
          rx={4}
        />

        {/* Stage labels */}
        <text x={x1 + COL_W / 2} y={H - 2} textAnchor="middle" className={styles.pipelineLabel}>
          {t('changeControl.postActions.status.QUEUED.label', { defaultValue: 'Queued' })}
        </text>
        <text x={x2 + COL_W / 2} y={H - 2} textAnchor="middle" className={styles.pipelineLabel}>
          {t('changeControl.postActions.status.RUNNING.label', { defaultValue: 'Running' })}
        </text>
        <text x={x3 + COL_W / 2} y={H - 2} textAnchor="middle" className={styles.pipelineLabel}>
          {t('changeControl.postActions.status.COMPLETED.label', {
            defaultValue: 'Done',
          })}
          {' / '}
          {t('changeControl.postActions.status.FAILED.label', {
            defaultValue: 'Failed',
          })}
        </text>
      </svg>

      <figcaption className={styles.srOnly}>{ariaSummary}</figcaption>

      <ul className={styles.pipelineCounts} aria-hidden="true">
        <li className={styles.pipelineCountQueued}>
          <span>{t('changeControl.postActions.status.QUEUED.label', { defaultValue: 'Queued' })}</span>
          <strong>{nf.format(counts.QUEUED)}</strong>
        </li>
        <li className={styles.pipelineCountRunning}>
          <span>{t('changeControl.postActions.status.RUNNING.label', { defaultValue: 'Running' })}</span>
          <strong>{nf.format(counts.RUNNING)}</strong>
        </li>
        <li className={styles.pipelineCountCompleted}>
          <span>{t('changeControl.postActions.status.COMPLETED.label', { defaultValue: 'Completed' })}</span>
          <strong>{nf.format(counts.COMPLETED)}</strong>
        </li>
        <li className={styles.pipelineCountFailed}>
          <span>{t('changeControl.postActions.status.FAILED.label', { defaultValue: 'Failed' })}</span>
          <strong>{nf.format(counts.FAILED)}</strong>
        </li>
      </ul>
    </figure>
  );
}

/**
 * Build a smooth cubic-bezier ribbon between two stage-rect anchors.
 * (x1, y1) is the top-start corner of the ribbon; (x2, y2) is the top-end
 * corner. `bandH` is the ribbon thickness.
 */
function pathBetween(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  bandH: number,
): string {
  const cx1 = x1 + (x2 - x1) * 0.5;
  const cx2 = x1 + (x2 - x1) * 0.5;
  const top = `M${x1},${y1} C${cx1},${y1} ${cx2},${y2} ${x2},${y2}`;
  const bot = `L${x2},${y2 + bandH} C${cx2},${y2 + bandH} ${cx1},${y1 + bandH} ${x1},${y1 + bandH} Z`;
  return `${top} ${bot}`;
}
